import type { TOC } from '@ember/component/template-only';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { guidFor } from '@ember/object/internals';
import { service } from '@ember/service';
import { buildWaiter } from '@ember/test-waiters';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { format, isValid, parseISO } from 'date-fns';
import { didCancel, dropTask, enqueueTask, restartableTask, task, timeout } from 'ember-concurrency';
import { runTask } from 'ember-lifeline';
import { modifier } from 'ember-modifier';
import svgJar from 'ember-svg-jar/helpers/svg-jar';
import { and, eq, not, or } from 'ember-truth-helpers';
import DirectUploader from 'garaje/components/direct-uploader';
import EnvoyOneUpCalendar from 'garaje/components/envoy-one-up-calendar';
import UiCard from 'garaje/components/ui/card';
import DocumentDeleteConfirmationModal from 'garaje/components/visitors/document-delete-confirmation-modal';
import DocumentReviewActions from 'garaje/components/visitors/document-review-actions';
import type { DocumentStatus } from 'garaje/components/visitors/document-status-badge';
import DocumentStatusBadge from 'garaje/components/visitors/document-status-badge';
import DocumentPreviewModal from 'garaje/components/visitors/profiles/document-preview-modal';
import type { ProfileDocument } from 'garaje/components/visitors/profiles/document-status';
import type CustomUserDocumentTemplateModel from 'garaje/models/custom-user-document-template';
import type DraftDocumentModel from 'garaje/models/draft-document';
import type { DraftDocumentFieldValues, DraftDocumentStatus } from 'garaje/models/draft-document';
import type UserDocumentTemplateConfigurationModel from 'garaje/models/user-document-template-configuration';
import type VisitorDocumentModel from 'garaje/models/visitor-document';
import { uploadFile } from 'garaje/utils/document-uploads';
import { confirmDraftDocument, pollDraftDocumentWithDeadline, requestUploadLink } from 'garaje/utils/draft-documents';
import {
  confirmVisitorDocument,
  pollVisitorDocumentWithDeadline,
  requestVisitorDocUploadLink,
  type VisitType,
} from 'garaje/utils/visitor-documents';
import type { ValidationResult } from 'utils/direct-uploader';

// Re-exported so the list and tests share one shape.
export type { DraftDocumentFieldValues, DraftDocumentStatus };

// The backend allows 100 MB; this is a tighter client cap.
const ACCEPTED_FILE_TYPES = '.pdf,.jpg,.jpeg,.png';
const ACCEPTED_MIME_REGEX = /^(application\/pdf|image\/jpeg|image\/png)$/;
const MAX_FILE_SIZE_BYTES = 10_000_000;

// `settled()` observes neither the raw-XHR S3 PUT nor ember-concurrency's `timeout()`, so without
// this a test can resume mid-upload, before `@onDraftChange` reports the draft id.
const uploadWaiter = buildWaiter('document-review-card:uploadDraftTask');

// Download/Open: the hover overlay on desktop, a plain row on phone where the preview is hidden.
const MediaActions: TOC<{ Args: { href: string; onOpen: () => void } }> = <template>
  <a
    href={{@href}}
    download
    target="_blank"
    rel="noopener noreferrer"
    class="btn btn--small btn--outline flex items-center gap-2 !bg-white !rounded-md pointer-events-auto sm:!bg-carbon-5"
    data-test-download-button
  >
    {{svgJar "download" width="14" height="14" class="shrink-0 -mt-1"}}
    <span>Download</span>
  </a>
  <button
    type="button"
    class="btn btn--small btn--outline flex items-center gap-2 !bg-white !rounded-md pointer-events-auto sm:!bg-carbon-5"
    {{on "click" @onOpen}}
    data-test-open-button
  >
    {{svgJar "expand-corners" width="12" height="12" class="shrink-0 -mt-1"}}
    <span>Open</span>
  </button>
</template>;

type ExpirationEntry = { name: string; date: Date | null };
/** A row with both a name and a parsed date. */
type CompleteExpirationEntry = { name: string; date: Date };

/** Any row with exactly one of name/date filled. Takes its argument so callers cannot re-seed. */
function listHasIncompleteRow(entries: readonly ExpirationEntry[]): boolean {
  return entries.some((e) => Boolean(e.name) !== (e.date !== null));
}

/** Drives the create-mode UI, not the draft's raw `status`, so a failed parse stays recoverable. */
type UploadPhase = 'idle' | 'uploading' | 'parsing' | 'completed' | 'error';

export type DocumentBusyKind = 'upload' | 'field-values-save' | 'incomplete';

/** Shared with DocumentReviewList. 'manage' adds upload/delete on top of 'review'. */
export type DocumentReviewMode = 'review' | 'create' | 'manage';

interface Signature {
  Args: {
    document?: VisitorDocumentModel;
    template?: CustomUserDocumentTemplateModel;
    configuration?: UserDocumentTemplateConfigurationModel;
    isUploaded?: boolean;
    showUndo?: boolean;
    onStatusChange?: () => void;
    mode?: DocumentReviewMode;
    /** Manage mode: reviewer-only. Gates Remove independently of the mode. Absent = no delete. */
    canDelete?: boolean;
    /** Reviewer-only. Gates Approve/Deny independently of the mode. Absent = no decision. */
    canDecide?: boolean;
    /** Create mode: required for the upload link. */
    locationId?: string | null;
    /** Create mode: the selected flow, sent as a relationship on confirm. */
    flowId?: string | null;
    /** Create mode: the draft id, reported as soon as it exists and `null` when none remains. */
    onDraftChange?: (templateId: string, draftId: string | null) => void;
    /** Create mode: blocks Submit until the draft id is collected. Parsing does not count. */
    onUploadStateChange?: (templateId: string, isUploading: boolean, kind: DocumentBusyKind) => void;
    /** Controlled overrides for isolated tests: take precedence over the self-driven draft. */
    status?: DraftDocumentStatus;
    fieldValues?: DraftDocumentFieldValues;
    /** Test seam: shrink the parse-poll interval/timeout so loop and timeout paths run fast. */
    pollOptions?: { intervalMs?: number; timeoutMs?: number };
    /** Manage mode: which visit (invite XOR entry) a direct upload attaches to. */
    visitType?: VisitType;
    /** Manage mode: the id of the visit the upload attaches to. */
    visitId?: string;
  };
  Element: HTMLDivElement;
}

export default class DocumentReviewCard extends Component<Signature> {
  @service declare store: StoreService;

  @tracked expirationEntries: ExpirationEntry[] = [];
  /** Index of the open calendar popover, or null. */
  @tracked openCalendar: number | null = null;
  /** A half-filled row is normal mid-entry, so the error holds off until focus leaves. */
  @tracked isEditingExpirationRows = false;
  #expirationFieldsElement: HTMLElement | null = null;

  // ----- create-mode state -----
  @tracked draft: DraftDocumentModel | null = null;
  @tracked selectedFile: File | null = null;
  @tracked previewDataUrl: string | null = null;
  /** Blob URL, since Chrome refuses a base64 `data:` PDF in an iframe. Revoke on teardown. */
  @tracked previewObjectUrl: string | null = null;
  @tracked uploadPhase: UploadPhase = 'idle';
  @tracked uploadErrorMessage: string | null = null;
  @tracked saveErrorMessage: string | null = null;
  /** Last state reported to the spine, to dedupe notifications. */
  #busyReported: Record<DocumentBusyKind, boolean> = {
    upload: false,
    'field-values-save': false,
    incomplete: false,
  };

  /** Exposed for the dropzone template. */
  readonly acceptedFileTypes = ACCEPTED_FILE_TYPES;
  readonly acceptedMimeRegex = ACCEPTED_MIME_REGEX;
  readonly maxFileSizeBytes = MAX_FILE_SIZE_BYTES;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    this.#initExpirationEntries();
  }

  get isCreateMode(): boolean {
    return this.args.mode === 'create';
  }

  get isManageMode(): boolean {
    return this.args.mode === 'manage';
  }

  /** Per-template test seam for the create form. */
  get adminPreregTemplateId(): string | undefined {
    return this.isCreateMode ? this.args.template?.id : undefined;
  }

  // ----- create-mode display state -----
  /** Live path uses `uploadPhase`; isolated tests drive `@status` instead. */
  get createCompleted(): boolean {
    return this.isCreateMode && (this.args.status === 'completed' || this.uploadPhase === 'completed');
  }

  get createInProgress(): boolean {
    if (!this.isCreateMode) return false;
    return (
      this.args.status === 'processing' ||
      this.args.status === 'pending' ||
      this.uploadPhase === 'uploading' ||
      this.uploadPhase === 'parsing'
    );
  }

  /** Recoverable failure: upload error or parse timeout. Controlled tests have no error state. */
  get createErrored(): boolean {
    return this.isCreateMode && this.uploadPhase === 'error';
  }

  /** The `completed` check closes the window where a second drop would duplicate the doc. */
  get showManageUpload(): boolean {
    return this.isManageMode && !this.args.document && this.uploadPhase !== 'completed';
  }

  /** Drives the parsing indicator in place of the dropzone. */
  get manageUploadInProgress(): boolean {
    return this.isManageMode && (this.uploadPhase === 'uploading' || this.uploadPhase === 'parsing');
  }

  /** Upload landed but the parent reload did not, so offer a retry instead of "not uploaded". */
  get manageUploadAwaitingReload(): boolean {
    return this.isManageMode && !this.args.document && this.uploadPhase === 'completed';
  }

  /** Re-runs the parent reload. Safe to repeat: it reloads, it does not re-upload. */
  retryManageReload = (): void => {
    this.args.onStatusChange?.();
  };

  /** Unique per card, so sibling dropzones do not collide on DirectUploader's default id. */
  get uploadInputId(): string {
    return `draft-upload-${this.args.template?.id ?? 'new'}`;
  }

  /** A button, not a `<label for>`: labels are not tab stops, and the input is hidden below `sm`. */
  openFilePicker = (): void => {
    document.getElementById(this.uploadInputId)?.click();
  };

  /** Names the hidden file input per document, so each dropzone is distinct to screen readers. */
  get uploadInputAriaLabel(): string {
    const name = this.args.template?.title ?? 'document';
    return `Upload ${name} (PDF, JPG, or PNG, 10 MB max)`;
  }

  /** Explains the lone blank row after a parse that found nothing. */
  get parseReturnedNoDates(): boolean {
    if (!this.createCompleted) return false;
    const [only] = this.expirationEntries;
    return this.expirationEntries.length === 1 && !only?.name && !only?.date;
  }

  #parseDate(dateStr: string): Date | null {
    // Extract date portion to avoid UTC off-by-one for ISO timestamps
    // e.g. "2026-10-15T00:00:00.000Z" should parse as Oct 15, not Oct 14 in PST
    const dateMatch = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
    if (dateMatch) {
      const d = new Date(Number(dateMatch[1]), Number(dateMatch[2]) - 1, Number(dateMatch[3]));
      return isNaN(d.getTime()) ? null : d;
    }
    const parsed = parseISO(dateStr);
    return isValid(parsed) ? parsed : null;
  }

  /** Parses a free-form `{ label -> date-string }` map into rows with both fields set. */
  #parseEntries(data: Record<string, unknown>): CompleteExpirationEntry[] {
    return Object.entries(data)
      .filter(([, v]) => typeof v === 'string')
      .map(([name, dateStr]) => ({ name, date: this.#parseDate(String(dateStr)) }))
      .filter((e): e is CompleteExpirationEntry => e.date !== null);
  }

  /** One-shot: a later auto-save reassigns `inputFieldsData` and would clobber edits. */
  #seededFromDocument = false;

  #initExpirationEntries(): void {
    const parsed = this.#parseEntries(this.args.document?.inputFieldsData ?? {});
    // Mounting with an already-parsed doc seeds the real rows here, so the reactive re-seed no-ops.
    if (parsed.length > 0) this.#seededFromDocument = true;
    this.#setExpirationEntries(
      parsed.length === 0 && this.requiresExpirationDate ? [{ name: '', date: null }] : parsed,
    );
  }

  /** Always leaves one row to hand-enter: an empty parse is valid, not a failure. */
  #seedExpirationEntriesFromMap(data: Record<string, unknown>): void {
    const parsed = this.#parseEntries(data);
    this.#setExpirationEntries(parsed.length > 0 ? parsed : [{ name: '', date: null }]);
  }

  /** Tests only. The live path seeds in `uploadDraftTask`, or auto-saves would re-seed. */
  seedFromControlledArgs = modifier(
    (
      _element: Element,
      [status, fieldValues]: [DraftDocumentStatus | undefined, DraftDocumentFieldValues | undefined],
    ) => {
      if (!this.isCreateMode || status !== 'completed') return;
      this.#seedExpirationEntriesFromMap(fieldValues ?? {});
    },
  );

  /** The constructor covers mount; a parent reload reuses this instance, so dates need seeding. */
  seedFromDocumentData = modifier((_element: Element, [data]: [Record<string, unknown> | undefined]) => {
    if (this.isCreateMode || this.#seededFromDocument) return;
    this.#seededFromDocument = true;
    const parsed = this.#parseEntries(data ?? {});
    if (parsed.length > 0) this.#setExpirationEntries(parsed);
  });

  get title(): string | undefined {
    return this.args.isUploaded ? this.args.document?.title : this.args.template?.title;
  }

  get status(): DocumentStatus {
    if (!this.args.isUploaded) return 'not-uploaded';
    const doc = this.args.document;
    if (doc?.isApproved) return 'approved';
    if (doc?.isDenied) return 'denied';
    // Manage is a superset of review: a still-pending doc keeps 'review' so the reviewer can act.
    return 'review';
  }

  /** The create form has no badge: its empty state is the dropzone, and APPROVED belongs to review. */
  get showStatusBadge(): boolean {
    return !this.isCreateMode;
  }

  get uploadDate(): string | null {
    const attachment = this.args.document?.mostRecentAttachment;
    const date = attachment?.updatedAt;
    if (!date) {
      const docDate = this.args.document?.updatedAt;
      return docDate ? format(docDate, 'MMM d, yyyy, h:mm a') : null;
    }
    return format(date, 'MMM d, yyyy, h:mm a');
  }

  get externalAttachmentUrl(): string | null {
    return this.args.document?.externalAttachments?.[0]?.url ?? null;
  }

  @tracked imageFailedToLoad = false;

  get isPdf(): boolean {
    return this.imageFailedToLoad;
  }

  /** False for PDFs, and for images whose FileReader preview produced no data URL. */
  get hasImagePreview(): boolean {
    return Boolean(this.selectedFile?.type.startsWith('image/')) && this.previewDataUrl !== null;
  }

  /** Carries the same viewer-chrome hash as the review iframe, so the drop renders as a page. */
  get createPreviewPdfUrl(): string | null {
    return this.previewObjectUrl ? `${this.previewObjectUrl}#toolbar=0&navpanes=0&scrollbar=0&view=FitH` : null;
  }

  /** Anonymized by the backend, so the preview area renders the discarded placeholder. */
  get isDiscarded(): boolean {
    // Redacted Veriff docs have an empty externalAttachments array, so the per-attachment flag
    // alone would miss them.
    return !!(this.args.document?.anonymizedAt ?? this.args.document?.externalAttachments?.[0]?.anonymized_at);
  }

  get mediaOverlayClass(): string {
    return 'absolute inset-0 flex items-center justify-center gap-2 bg-black/0 opacity-0 group-hover:bg-black/40 group-hover:opacity-100 transition-all duration-200 pointer-events-none';
  }

  handleImageError = (): void => {
    this.imageFailedToLoad = true;
  };

  get pdfViewerUrl(): string {
    // view=FitH fills the frame width; without it the page letterboxes and the gray band sits below.
    return `${this.externalAttachmentUrl}#navpanes=0&toolbar=0&scrollbar=0&view=FitH`;
  }

  get documentFileName(): string {
    return this.title ?? 'document.pdf';
  }

  @tracked showPreviewModal = false;

  get previewDocuments(): ProfileDocument[] {
    return [this.previewDocument];
  }

  get previewDocument(): ProfileDocument {
    return {
      id: this.args.document?.id ?? '',
      title: this.title ?? '',
      downloadUrl: this.externalAttachmentUrl ?? undefined,
      approvalStatus: this.args.document?.isApproved ? 'approved' : this.args.document?.isDenied ? 'denied' : 'review',
      createdAt: this.args.document?.createdAt?.toISOString(),
      fieldValues: this.args.document?.inputFieldsData,
    };
  }

  get canOpenPreview(): boolean {
    return Boolean(this.args.isUploaded && !this.isDiscarded && this.externalAttachmentUrl);
  }

  openDocument = (): void => {
    this.showPreviewModal = true;
  };

  closePreviewModal = (): void => {
    this.showPreviewModal = false;
  };

  get documentLink(): VisitorDocumentModel['userDocumentLink'] {
    return this.args.document?.userDocumentLink;
  }

  get isPending(): boolean {
    return this.status === 'review';
  }

  get requiresExpirationDate(): boolean {
    return this.args.configuration?.reUploadConfig?.policy === 'on_expiration';
  }

  /** When off the backend never parses, so polling would hang until the timeout. */
  get llmParsingEnabled(): boolean {
    return this.args.configuration?.allowLlmParsing ?? this.args.template?.allowLlmParsing ?? false;
  }

  /** Rows render for an uploaded doc in review, or a completed draft in create. */
  get showExpirationRows(): boolean {
    if (this.isCreateMode) return this.createCompleted;
    return Boolean(this.args.isUploaded);
  }

  get parsingLabel(): string {
    return this.uploadPhase === 'uploading' ? 'Uploading…' : 'Parsing document…';
  }

  get hasAnyCompleteDate(): boolean {
    return this.expirationEntries.some((e) => e.name && e.date);
  }

  get approveDisabled(): boolean {
    if (this.approveTask.isRunning) return true;
    if (this.requiresExpirationDate && !this.hasAnyCompleteDate) return true;
    if (this.hasIncompleteExpirationEntries) return true;
    return false;
  }

  formatDate = (date: Date | null): string => (date ? format(date, 'MMM d, yyyy') : 'Select date');

  toggleCalendar = (id: number): void => {
    if (this.openCalendar === id) {
      this.closeCalendar();
    } else {
      this.openCalendar = id;
      // Escape binds synchronously, but the outside-click listener waits a frame so the opening
      // click cannot bubble into it and close the popover immediately.
      document.addEventListener('keydown', this.handleEscapeKey, true);
      requestAnimationFrame(() => {
        if (this.isDestroying || this.isDestroyed) return;
        document.addEventListener('click', this.handleOutsideClick, true);
      });
    }
  };

  captureExpirationFields = modifier((element: Element) => {
    this.#expirationFieldsElement = element as HTMLElement;
    const onFocusIn = (): void => {
      this.isEditingExpirationRows = true;
    };
    const onFocusOut = (event: Event): void => {
      if (element.contains((event as FocusEvent).relatedTarget as Node)) return;
      this.isEditingExpirationRows = false;
    };
    element.addEventListener('focusin', onFocusIn);
    element.addEventListener('focusout', onFocusOut);
    return () => {
      element.removeEventListener('focusin', onFocusIn);
      element.removeEventListener('focusout', onFocusOut);
    };
  });

  /** Deferred so it does not fire between the name and the date. Approve still gates at once. */
  get showIncompleteRowError(): boolean {
    return this.hasIncompleteExpirationEntries && !this.isEditingExpirationRows;
  }

  handleOutsideClick = (event: MouseEvent): void => {
    if (this.#expirationFieldsElement?.contains(event.target as Node)) return;
    this.closeCalendar();
  };

  /** Closes the calendar and returns focus to the trigger, per the WAI-ARIA dialog pattern. */
  handleEscapeKey = (event: KeyboardEvent): void => {
    if (event.key !== 'Escape') return;
    const triggerIndex = this.openCalendar;
    event.stopPropagation();
    this.closeCalendar();
    if (triggerIndex === null) return;
    runTask(this, () => {
      const triggers = this.#expirationFieldsElement?.querySelectorAll<HTMLElement>('[data-test-expiration-date]');
      triggers?.[triggerIndex]?.focus();
    });
  };

  closeCalendar = (): void => {
    this.openCalendar = null;
    document.removeEventListener('click', this.handleOutsideClick, true);
    document.removeEventListener('keydown', this.handleEscapeKey, true);
  };

  willDestroy(): void {
    super.willDestroy();
    document.removeEventListener('click', this.handleOutsideClick, true);
    document.removeEventListener('keydown', this.handleEscapeKey, true);
    // An upload can still be in flight here, so retract the report or the spine's Submit gate
    // stays disabled forever.
    this.#reportUploadInFlight(false);
    this.#reportUploadInFlight(false, 'field-values-save');
    this.#reportUploadInFlight(false, 'incomplete');
    // Stops the round-trip too; the task's own isDestroying guard is the primary protection.
    void this.deleteDocumentTask.cancelAll();
    this.#setPreviewObjectUrl(null);
  }

  /** Revokes the prior blob URL first, so the PDF object is not leaked across re-selections. */
  #setPreviewObjectUrl(url: string | null): void {
    if (this.previewObjectUrl) URL.revokeObjectURL(this.previewObjectUrl);
    this.previewObjectUrl = url;
  }

  get isDecided(): boolean {
    return this.status === 'approved' || this.status === 'denied';
  }

  addExpirationRow = (): void => {
    this.#setExpirationEntries([...this.expirationEntries, { name: '', date: null }]);
  };

  get canRemoveRow(): boolean {
    return !this.requiresExpirationDate || this.expirationEntries.length > 1;
  }

  removeExpirationRow = (index: number): void => {
    if (!this.canRemoveRow) return;
    this.#setExpirationEntries(this.expirationEntries.filter((_, i) => i !== index));
    void this.saveExpirationDatesTask.perform();
    // The focused remove button is about to be destroyed, so move focus to the successor row (or
    // Add) once the rows re-render, rather than dropping keyboard users on <body>.
    runTask(this, () => {
      const container = this.#expirationFieldsElement;
      if (!container) return;
      const removeButtons = container.querySelectorAll<HTMLElement>('[data-test-remove-expiration]');
      const successor = removeButtons[Math.min(index, removeButtons.length - 1)];
      (successor ?? container.querySelector<HTMLElement>('[data-test-add-expiration]'))?.focus();
    });
  };

  updateExpirationName = (index: number, event: Event): void => {
    const value = (event.target as HTMLInputElement).value;
    this.#setExpirationEntries(this.expirationEntries.map((e, i) => (i === index ? { ...e, name: value } : e)));
    void this.saveExpirationDatesTask.perform();
  };

  selectExpirationDate = (index: number, date: Date): void => {
    this.#setExpirationEntries(this.expirationEntries.map((e, i) => (i === index ? { ...e, date } : e)));
    this.closeCalendar();
    void this.saveExpirationDatesTask.perform();
  };

  /** Builds a fresh `{ label -> yyyy-MM-dd }` map, never mutating the frozen `fieldValues`. */
  #buildInputFieldsData(): Record<string, string> {
    const data: Record<string, string> = {};
    for (const entry of this.expirationEntries) {
      if (entry.name && entry.date) {
        data[entry.name] = format(entry.date, 'yyyy-MM-dd');
      }
    }
    return data;
  }

  get hasIncompleteExpirationEntries(): boolean {
    return listHasIncompleteRow(this.expirationEntries);
  }

  /** The one write path for expirationEntries, so no mutation site can skip the incomplete signal. */
  #setExpirationEntries(next: ExpirationEntry[]): void {
    this.expirationEntries = next;
    this.#reportUploadInFlight(listHasIncompleteRow(next), 'incomplete');
  }

  /** Create mode PATCHes the draft via its model apiAction; review mode the visitor-document. */
  async #persistFieldValues(): Promise<void> {
    if (this.isCreateMode) {
      if (!this.draft) return;
      await this.draft.updateFieldValues(this.#buildInputFieldsData());
      return;
    }
    await this.#patchInputFieldsData();
  }

  /** `active` is sent to preserve it, since the response pushes all attributes into the store. */
  async #patchInputFieldsData(): Promise<void> {
    const doc = this.args.document;
    if (!doc) return;
    const data = this.#buildInputFieldsData();
    const adapter = this.store.adapterFor('visitor-document');
    const url = adapter.buildURL('visitor-document', doc.id, null, 'v3');
    await adapter.ajax(url, 'PATCH', {
      data: {
        data: {
          type: 'visitor-documents',
          id: doc.id,
          attributes: {
            'input-fields-data': data,
            active: doc.active,
          },
        },
      },
    });
    doc.inputFieldsData = Object.freeze({ ...data });
  }

  /** Enqueued, not restartable: a restart could let an older full-map PATCH land last. */
  saveExpirationDatesTask = enqueueTask(async () => {
    // The convert reads field-values server-side, so a submit must not race this PATCH or an
    // auto-approved doc carries stale dates. Cleared once the queue drains.
    this.#reportUploadInFlight(true, 'field-values-save');
    try {
      if (this.hasIncompleteExpirationEntries) return;
      this.saveErrorMessage = null;
      await this.#persistFieldValues();
    } catch (e) {
      if (didCancel(e)) return;
      this.saveErrorMessage = 'Could not save expiration dates. Please try again.';
      throw e;
    } finally {
      if (!this.saveExpirationDatesTask.isQueued) this.#reportUploadInFlight(false, 'field-values-save');
    }
  });

  // ----- create-mode upload/parse lifecycle (per-card) -----

  /** Notifies the spine on change only, so it can disable Submit while this card is busy. */
  #reportUploadInFlight(isUploading: boolean, kind: DocumentBusyKind = 'upload'): void {
    const templateId = this.args.template?.id;
    if (!this.isCreateMode || !templateId) return;
    if (this.#busyReported[kind] === isUploading) return;
    this.#busyReported[kind] = isUploading;
    this.args.onUploadStateChange?.(templateId, isUploading, kind);
  }

  handleValidate = (result: ValidationResult): void => {
    if (!result.sizeValid) this.uploadErrorMessage = 'The file must be 10 MB or less.';
    else if (!result.typeValid) this.uploadErrorMessage = 'The file must be a PDF, JPG, or PNG.';
    else this.uploadErrorMessage = null;
  };

  handleFilePreview = (result: FileReader['result']): void => {
    this.previewDataUrl = typeof result === 'string' ? result : null;
  };

  /** DirectUploader only invokes this for files that passed validation. */
  handleFileSelected = (file: File | null): void => {
    if (!file) return;
    // Manage mode uploads straight to the visit: no draft, and onStatusChange reloads the tab.
    if (this.isManageMode) {
      this.uploadPhase = 'uploading';
      void this.uploadVisitorDocTask.perform(file);
      return;
    }
    this.selectedFile = file;
    // Only PDFs need an object URL; images use the data-URL preview.
    this.#setPreviewObjectUrl(file.type === 'application/pdf' ? URL.createObjectURL(file) : null);
    // Synchronous so the dropzone does not flicker back before the task starts.
    this.uploadPhase = 'uploading';
    this.#reportUploadInFlight(true);
    void this.uploadDraftTask.perform(file);
  };

  resetFile = (): void => {
    void this.uploadDraftTask.cancelAll();
    // A save that fails after the reset would set saveErrorMessage on the now-empty card.
    void this.saveExpirationDatesTask.cancelAll();
    this.selectedFile = null;
    this.previewDataUrl = null;
    this.#setPreviewObjectUrl(null);
    this.draft = null;
    this.#setExpirationEntries([]);
    this.uploadErrorMessage = null;
    this.saveErrorMessage = null;
    this.uploadPhase = 'idle';
    this.#reportUploadInFlight(false);
    this.#reportUploadInFlight(false, 'field-values-save');
    if (this.args.template) this.args.onDraftChange?.(this.args.template.id, null);
    // The focused Remove button is about to be destroyed, so return focus to the input.
    runTask(this, () => {
      document.getElementById(this.uploadInputId)?.focus();
    });
  };

  /** Per-row, so a screen-reader user can tell which of several rows they are on. */
  expirationNameLabel = (index: number): string => `Expiration date name, row ${index + 1}`;

  /** Index only: data-private does not mask aria-label, so the field name must not appear here. */
  expirationDateLabel = (index: number): string => `Expiration date, row ${index + 1}`;

  /** Card-unique via guidFor, so a half-filled row's aria-describedby points at its own reason. */
  expirationNameErrorId = (index: number): string => `${guidFor(this)}-exp-name-error-${index}`;

  /** On the card so cancellation stays task-linked. Rejects on timeout so a stuck parse surfaces. */
  pollUntilTerminalTask = task(async (draftDocument: DraftDocumentModel) => {
    const { intervalMs = 1000, timeoutMs = 30000 } = this.args.pollOptions ?? {};
    const startedAt = Date.now();
    let current = draftDocument;

    while (!current.isCompleted) {
      const remaining = timeoutMs - (Date.now() - startedAt);
      if (remaining <= 0) {
        throw new Error(`Draft document ${current.id} parsing timed out after ${timeoutMs}ms`);
      }
      await timeout(Math.min(intervalMs, remaining));
      // Bounded by the remaining budget, so a hung GET cannot skip the next deadline check.
      current = await pollDraftDocumentWithDeadline(
        this.store,
        current.id,
        Math.max(timeoutMs - (Date.now() - startedAt), 0),
      );
    }

    return current;
  });

  /** Failures land in `error` and retract the id, so an unconfirmed doc is never submitted. */
  uploadDraftTask = restartableTask(async (file: File) => {
    const waiterToken = uploadWaiter.beginAsync();
    // EC cancellation stops this task at its awaits but cannot reach the raw-XHR PUT, so `finally`
    // aborts it. Aborting a settled PUT is a no-op.
    const uploadAbort = new AbortController();
    try {
      this.uploadErrorMessage = null;
      const template = this.args.template;
      const flowId = this.args.flowId;
      if (!template || !flowId) {
        this.uploadPhase = 'error';
        // A missing flowId means the flow has not resolved, which the admin can fix; a missing
        // template is a programming error. Both land in the recoverable error state.
        this.uploadErrorMessage = !flowId
          ? 'Select a visitor type before uploading a document.'
          : 'Unable to upload right now. Please try again.';
        // The change handler already reported in-flight, so retract it or Submit stays disabled.
        this.#reportUploadInFlight(false);
        return;
      }
      // Run-local, so the catch branches on this run: `this.draft` may hold a stale draft.
      let created: DraftDocumentModel | null = null;
      try {
        this.uploadPhase = 'uploading';
        // Sequenced here rather than in a util, so each await is a cancellation point of this task.
        const { draftDocumentId, uploadLink } = await requestUploadLink(this.store, {
          templateId: template.id,
          locationId: this.args.locationId,
          contentType: file.type,
          byteSize: file.size,
        });
        await uploadFile(uploadLink, file, { signal: uploadAbort.signal });
        created = await confirmDraftDocument(this.store, draftDocumentId, { templateId: template.id, flowId });
        this.draft = created;
        // The draft exists, so leave the pre-confirm window and re-enable Submit. Parsing does
        // not block it.
        this.#reportUploadInFlight(false);
        this.args.onDraftChange?.(template.id, created.id);

        let finalDraft = created;
        if (this.llmParsingEnabled) {
          this.uploadPhase = 'parsing';
          // A parse that found nothing still completes, with an empty map, giving one blank row.
          finalDraft = await this.pollUntilTerminalTask.perform(created);
          this.#seedExpirationEntriesFromMap(finalDraft.fieldValues ?? {});
        } else {
          // The backend never parses, so the draft would stay 'pending' and polling would time out.
          this.#seedExpirationEntriesFromMap({});
        }
        this.draft = finalDraft;
        this.uploadPhase = 'completed';
        this.args.onDraftChange?.(template.id, finalDraft.id);
      } catch (e) {
        if (didCancel(e)) return;
        // A created draft means the parse failed; otherwise the upload did. Retract either way.
        this.uploadPhase = 'error';
        this.uploadErrorMessage = created
          ? 'Parsing took too long. Remove the file and try again.'
          : 'Upload failed. Please try again.';
        this.#reportUploadInFlight(false);
        this.args.onDraftChange?.(template.id, null);
      }
    } finally {
      uploadAbort.abort();
      uploadWaiter.endAsync(waiterToken);
    }
  });

  /** Returns rather than throws on timeout: the doc exists, so a slow parse reconciles later. */
  pollVisitorDocUntilParsedTask = dropTask(async (visitorDocument: VisitorDocumentModel) => {
    const { intervalMs = 1000, timeoutMs = 30000 } = this.args.pollOptions ?? {};
    const startedAt = Date.now();
    let current = visitorDocument;
    while (!current.isParseComplete) {
      const remaining = timeoutMs - (Date.now() - startedAt);
      if (remaining <= 0) return current;
      await timeout(Math.min(intervalMs, remaining));
      current = await pollVisitorDocumentWithDeadline(
        this.store,
        current.id,
        Math.max(timeoutMs - (Date.now() - startedAt), 0),
      );
    }
    return current;
  });

  /** Restartable is safe: the PUT is aborted below, and a stray confirm shows as a deletable dupe. */
  uploadVisitorDocTask = restartableTask(async (file: File) => {
    const uploadAbort = new AbortController();
    // Once confirm resolves the doc exists and is auto-approved, so a later failure must reconcile
    // rather than invite a duplicate upload.
    let confirmed = false;
    // After the PUT, confirm may have committed the doc server-side even if its await rejects.
    let uploadedToS3 = false;
    try {
      this.uploadErrorMessage = null;
      const template = this.args.template;
      const visitType = this.args.visitType;
      const visitId = this.args.visitId;
      if (!template || !visitType || !visitId) {
        this.uploadPhase = 'error';
        this.uploadErrorMessage = 'Unable to upload right now. Please try again.';
        return;
      }
      try {
        this.uploadPhase = 'uploading';
        const { documentId, uploadLink } = await requestVisitorDocUploadLink(this.store, {
          visitType,
          visitId,
          templateId: template.id,
          contentType: file.type,
          byteSize: file.size,
        });
        await uploadFile(uploadLink, file, { signal: uploadAbort.signal });
        uploadedToS3 = true;
        const doc = await confirmVisitorDocument(this.store, documentId, {
          visitType,
          visitId,
          templateId: template.id,
        });
        confirmed = true;
        if (this.llmParsingEnabled) {
          this.uploadPhase = 'parsing';
          // The parse runs after confirm, so reloading before it lands would render empty rows.
          await this.pollVisitorDocUntilParsedTask.perform(doc);
        }
        this.uploadPhase = 'completed';
        this.args.onStatusChange?.();
      } catch (e) {
        if (didCancel(e)) return;
        if (confirmed) {
          // Only the post-confirm poll failed, so reload to render the doc rather than erroring.
          this.uploadPhase = 'completed';
          this.args.onStatusChange?.();
          return;
        }
        if (uploadedToS3 && (await this.#confirmCommittedDoc(visitType, visitId, template.id))) {
          // Confirm rejected client-side but committed server-side, so reload to render it.
          this.uploadPhase = 'completed';
          this.args.onStatusChange?.();
          return;
        }
        this.uploadPhase = 'error';
        this.uploadErrorMessage = 'Upload failed. Please try again.';
      }
    } finally {
      uploadAbort.abort();
    }
  });

  /** Re-queries the visit's documents to detect a doc the confirm committed before it rejected. */
  async #confirmCommittedDoc(visitType: VisitType, visitId: string, templateId: string): Promise<boolean> {
    try {
      const docs = await this.store.query('visitor-document', { filter: { [`${visitType}-id`]: visitId } });
      // eslint-disable-next-line ember/use-ember-get-and-set
      return docs.slice().some((d) => d.belongsTo('userDocumentTemplate').id() === templateId);
    } catch (e) {
      // Propagate cancellation so a superseded run stops instead of flashing a stale error.
      if (didCancel(e)) throw e;
      return false;
    }
  }

  approveTask = dropTask(async () => {
    try {
      // Through the FIFO queue, so this persist cannot overlap an in-flight edit save.
      await this.saveExpirationDatesTask.perform();
      await this.documentLink?.approve();
      this.args.onStatusChange?.();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }
  });

  denyTask = dropTask(async () => {
    try {
      void this.saveExpirationDatesTask.cancelAll();
      await this.documentLink?.deny();
      this.args.onStatusChange?.();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }
  });

  resetTask = dropTask(async () => {
    try {
      await this.documentLink?.reset();
      this.args.onStatusChange?.();
    } catch (e) {
      if (!didCancel(e)) throw e;
    }
  });

  /** A pending doc is not decision authority — a non-reviewer host reaches it. Absent `@canDecide` withholds both. */
  get showDecisionActions(): boolean {
    return Boolean(this.args.isUploaded) && !this.isCreateMode && this.isPending && (this.args.canDecide ?? false);
  }

  @tracked showDeleteConfirm = false;
  @tracked deleteErrorMessage: string | null = null;

  /** Manage mode is not delete authority — a non-reviewer host reaches it. Absent `@canDelete` withholds Remove. */
  get showDeleteAffordance(): boolean {
    return this.isManageMode && Boolean(this.args.document) && (this.args.canDelete ?? false);
  }

  openDeleteConfirm = (): void => {
    this.deleteErrorMessage = null;
    this.showDeleteConfirm = true;
  };

  /** Every dismissal routes here. Ignored mid-delete so a failure still renders in the open modal. */
  closeDeleteConfirm = (): void => {
    if (this.deleteDocumentTask.isRunning) return;
    this.showDeleteConfirm = false;
  };

  deleteDocumentTask = dropTask(async () => {
    const doc = this.args.document;
    if (!doc) return;
    // Fire-and-forget: an already-sent PATCH cannot be aborted, and a late save against the
    // destroyed doc 404s harmlessly, so awaiting it would only add latency.
    void this.saveExpirationDatesTask.cancelAll();
    this.deleteErrorMessage = null;
    try {
      // Cascades the doc off both the invite and the visitor profile.
      await doc.destroyRecord();
      // The round-trip can outlive the card, so do not write tracked state after teardown.
      if (this.isDestroying || this.isDestroyed) return;
      // The instance survives the reload (the list keys by template id), so a stale 'completed'
      // would keep showManageUpload false and leave no dropzone for a re-upload.
      this.uploadPhase = 'idle';
      this.showDeleteConfirm = false;
      this.args.onStatusChange?.();
    } catch (e) {
      if (didCancel(e)) return;
      // Back to a clean baseline so a retry re-issues DELETE instead of no-opping on a dirty record.
      doc.rollbackAttributes();
      if (!this.isDestroying && !this.isDestroyed) {
        this.deleteErrorMessage = 'Could not remove the document. Please try again.';
      }
      throw e; // surface to telemetry, matching the sibling tasks
    }
  });

  <template>
    <UiCard
      @border={{false}}
      class="rounded-lg p-4 mb-3 overflow-visible !bg-carbon-5 !shadow-none border border-carbon-10 sm:p-6 sm:mb-4"
      data-test-document-review-card
      data-test-admin-prereg-document={{this.adminPreregTemplateId}}
      ...attributes
    >
      {{! Create mode renders inside a "Required documents" <h3>, so its title is an <h4>. }}
      <div class="flex items-start justify-between mb-3 sm:mb-4">
        <div>
          {{#if this.isCreateMode}}
            <h4 class="text-sm font-medium m-0 sm:text-base" data-test-document-title>{{this.title}}</h4>
          {{else}}
            <h3 class="text-base font-semibold m-0 sm:text-lg" data-test-document-title>
              {{#if this.canOpenPreview}}
                {{! Gem on phone, where the title is how you reach the preview. }}
                <button
                  type="button"
                  class="text-left text-gem-60 hover:underline sm:text-current sm:hover:no-underline"
                  data-test-document-title-link
                  {{on "click" this.openDocument}}
                >
                  {{this.title}}
                </button>
              {{else}}
                {{this.title}}
              {{/if}}
            </h3>
          {{/if}}
          {{#if this.uploadDate}}
            <p class="text-xs text-carbon-50 mt-1 mb-0 sm:text-sm" data-test-upload-date>
              Uploaded
              {{this.uploadDate}}
            </p>
          {{/if}}
          {{#if this.showStatusBadge}}
            {{! -ml-1 cancels UiPill's own ml-1 so the badge sits flush left. }}
            <div class="mt-2 -ml-1">
              <DocumentStatusBadge @status={{this.status}} />
            </div>
          {{/if}}
        </div>
        {{#if this.showDeleteAffordance}}
          <button
            type="button"
            class="shrink-0 -mt-1 -mr-1 flex items-center justify-center p-1 rounded text-carbon-70 transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gem-60 focus-visible:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
            data-test-document-delete
            aria-label="Remove document"
            disabled={{this.deleteDocumentTask.isRunning}}
            {{on "click" this.openDeleteConfirm}}
          >
            {{svgJar "trash-16" width="20" height="20" class="[&_path]:fill-current"}}
          </button>
        {{else if (and this.isCreateMode (or this.createCompleted this.createErrored))}}
          {{! Same affordance as the manage delete, but this resets the draft. }}
          <button
            type="button"
            class="shrink-0 -mt-1 -mr-1 flex items-center justify-center p-1 rounded text-carbon-70 transition-opacity hover:opacity-75 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gem-60 focus-visible:ring-offset-2"
            data-test-remove-draft
            aria-label="Remove uploaded document"
            {{on "click" this.resetFile}}
          >
            {{svgJar "trash-16" width="20" height="20" class="[&_path]:fill-current"}}
          </button>
        {{/if}}
      </div>

      {{#if this.isCreateMode}}
        {{! ===== CREATE MODE: dropzone -> upload -> parse -> rows ===== }}

        {{! Present-then-filled, not inserted-with-content, for reliable screen-reader delivery. }}
        <span class="sr-only" role="status" aria-live="polite" data-test-draft-status-announcement>
          {{if this.createInProgress this.parsingLabel}}{{if
            this.createCompleted
            "Document ready. Review the expiration dates below."
          }}
        </span>

        {{! Hoisted so every state shares one copy, clipped to a 166px peek of the document top. }}
        {{#if this.selectedFile}}
          <div class="rounded-lg bg-carbon-10 p-2 mb-4" data-test-draft-preview>
            <div class="relative rounded-md overflow-hidden bg-white">
              {{#if this.hasImagePreview}}
                <img
                  src={{this.previewDataUrl}}
                  alt="Preview of uploaded document"
                  class="w-full h-[166px] object-cover object-top"
                  data-private
                />
              {{else if this.createPreviewPdfUrl}}
                <iframe
                  src={{this.createPreviewPdfUrl}}
                  title="Document preview"
                  class="w-full h-[166px] border-0 pointer-events-none"
                  data-private
                ></iframe>
              {{else}}
                {{! Fallback (URL not yet ready / unexpected type): name the file rather than show nothing. }}
                <div class="flex items-center gap-3 p-4">
                  <span class="flex-none">{{svgJar "document" height="24"}}</span>
                  <span class="flex-1 font-medium text-carbon-70 overflow-hidden" data-test-draft-filename data-private>
                    {{this.selectedFile.name}}
                  </span>
                </div>
              {{/if}}
              {{#if (or this.hasImagePreview this.createPreviewPdfUrl)}}
                {{! Lifts gray over the document so the preview reads as a peek. }}
                <div
                  class="absolute inset-x-0 bottom-0 h-12 bg-carbon-10"
                  aria-hidden="true"
                  data-test-draft-preview-fade
                ></div>
              {{/if}}
            </div>
          </div>
        {{/if}}

        {{#if (or this.createCompleted this.createErrored)}}
          {{! Intentionally empty: keeps the dropzone from re-appearing once a draft exists. }}
        {{else if this.createInProgress}}
          <p
            class="flex items-center gap-2 text-sm text-carbon-50 mt-0 mb-4"
            data-test-admin-prereg-document-parsing
            aria-hidden="true"
          >
            <img src="/assets/images/progress-small-gray.svg" alt="" aria-hidden="true" class="w-4 h-4" />
            {{this.parsingLabel}}
          </p>
        {{else}}
          {{! DirectUploader handles drag/drop, type and size validation, and preview. }}
          <DirectUploader
            @accept={{this.acceptedFileTypes}}
            @validType={{this.acceptedMimeRegex}}
            @validSize={{this.maxFileSizeBytes}}
            @autoUpload={{false}}
            @enqueue={{false}}
            @showResetButton={{false}}
            @showErrorState={{false}}
            @onValidate={{this.handleValidate}}
            @onFilePreview={{this.handleFilePreview}}
            @onFileSelected={{this.handleFileSelected}}
            @inputId={{this.uploadInputId}}
            @inputAriaLabel={{this.uploadInputAriaLabel}}
            {{! bg-white, or the gray section shows through the transparent dropzone. }}
            class="hidden h-40 bg-white !border-solid border-carbon-10 sm:flex"
            data-test-draft-dropzone
          >
            <:label>
              {{! for/id gives the input an accessible name and makes the text open the picker. }}
              <label for={{this.uploadInputId}} class="cursor-pointer">
                <span class="btn--link !text-gem-50 text-sm">Upload a document</span>
                <span class="!text-sm">or drag and drop here</span>
              </label>
            </:label>
          </DirectUploader>
          {{! Phone: replaces the dropzone square, which is hidden at this width. }}
          <button
            type="button"
            class="btn btn--small btn--outline !bg-white flex items-center justify-center w-full sm:hidden"
            data-test-upload-button
            {{on "click" this.openFilePicker}}
          >
            Upload a document
          </button>
          <p class="text-xs text-carbon-40 mt-2 mb-0 sm:text-sm" data-test-accepted-formats>
            Accepted formats: .pdf, .jpg, .png.
          </p>
        {{/if}}

        {{#if this.uploadErrorMessage}}
          <p class="text-xs text-red-50 mt-1 mb-2" data-test-draft-error role="alert" aria-live="assertive">
            {{this.uploadErrorMessage}}
          </p>
        {{/if}}

      {{else if @isUploaded}}
        {{! ===== REVIEW MODE: uploaded-document preview ===== }}
        {{#if this.isDiscarded}}
          <div class="rounded-md border border-carbon-10 overflow-hidden mb-4 bg-carbon-5" data-test-document-discarded>
            <div class="w-full min-h-80 flex items-center justify-center">
              <span class="text-carbon-70 text-sm font-normal italic">Document discarded</span>
            </div>
          </div>
        {{else if this.externalAttachmentUrl}}
          {{! Phone: the preview is behind the title/Open click instead of inline. }}
          <div class="hidden rounded-lg bg-carbon-10 p-2 mb-4 sm:block" data-test-document-preview>
            <div class="group relative rounded-md overflow-hidden bg-white">
              {{#if this.isPdf}}
                <iframe
                  src={{this.pdfViewerUrl}}
                  title={{this.documentFileName}}
                  class="w-full h-[166px] border-0"
                  data-private
                />
                {{! Captures hover over the iframe so group-hover fires. }}
                <div class="absolute inset-0"></div>
              {{else}}
                <img
                  src={{this.externalAttachmentUrl}}
                  alt={{this.title}}
                  class="w-full h-[166px] object-cover object-top"
                  data-private
                  {{on "error" this.handleImageError}}
                />
              {{/if}}
              {{! Lifts gray over the document so the preview reads as a peek. }}
              <div
                class="absolute inset-x-0 bottom-0 h-12 bg-carbon-10 pointer-events-none"
                aria-hidden="true"
                data-test-document-preview-fade
              ></div>
              <div class={{this.mediaOverlayClass}}>
                <MediaActions @href={{this.externalAttachmentUrl}} @onOpen={{this.openDocument}} />
              </div>
            </div>
          </div>

          {{! Phone: the same pair as buttons on the row, since there is no hover. }}
          <div class="flex gap-2 mb-3 sm:hidden" data-test-document-media-actions>
            <MediaActions @href={{this.externalAttachmentUrl}} @onOpen={{this.openDocument}} />
          </div>

          {{#if this.showPreviewModal}}
            <DocumentPreviewModal
              @documents={{this.previewDocuments}}
              @initialIndex={{0}}
              @onClose={{this.closePreviewModal}}
              @showActions={{this.showDecisionActions}}
            >
              <:actions>
                {{#if this.showDecisionActions}}
                  <DocumentReviewActions
                    @stickyBar={{true}}
                    @onApprove={{this.approveTask.perform}}
                    @onDeny={{this.denyTask.perform}}
                    @approveDisabled={{this.approveDisabled}}
                    @denyDisabled={{this.denyTask.isRunning}}
                  />
                {{/if}}
              </:actions>
            </DocumentPreviewModal>
          {{/if}}
        {{/if}}
      {{else if this.showManageUpload}}
        {{! ===== MANAGE MODE: direct upload; the tab reload shows the doc ===== }}
        <span class="sr-only" role="status" aria-live="polite" data-test-manage-upload-announcement>
          {{if this.manageUploadInProgress this.parsingLabel}}
        </span>

        {{#if this.manageUploadInProgress}}
          <p
            class="flex items-center gap-2 text-sm text-carbon-50 mt-0 mb-4"
            data-test-admin-prereg-document-parsing
            aria-hidden="true"
          >
            <img src="/assets/images/progress-small-gray.svg" alt="" aria-hidden="true" class="w-4 h-4" />
            {{this.parsingLabel}}
          </p>
        {{else}}
          {{! Same dropzone as create. }}
          <DirectUploader
            @accept={{this.acceptedFileTypes}}
            @validType={{this.acceptedMimeRegex}}
            @validSize={{this.maxFileSizeBytes}}
            @autoUpload={{false}}
            @enqueue={{false}}
            @showResetButton={{false}}
            @showErrorState={{false}}
            @onValidate={{this.handleValidate}}
            @onFilePreview={{this.handleFilePreview}}
            @onFileSelected={{this.handleFileSelected}}
            @inputId={{this.uploadInputId}}
            @inputAriaLabel={{this.uploadInputAriaLabel}}
            class="hidden h-40 bg-white !border-solid border-carbon-10 sm:flex"
            data-test-manage-dropzone
          >
            <:label>
              <label for={{this.uploadInputId}} class="cursor-pointer">
                <span class="btn--link !text-gem-50 text-sm">Upload a document</span>
                <span class="!text-sm">or drag and drop here</span>
              </label>
            </:label>
          </DirectUploader>
          {{! Phone: replaces the dropzone square, which is hidden at this width. }}
          <button
            type="button"
            class="btn btn--small btn--outline !bg-white flex items-center justify-center w-full sm:hidden"
            data-test-upload-button
            {{on "click" this.openFilePicker}}
          >
            Upload a document
          </button>
          <p class="text-xs text-carbon-40 mt-2 mb-0 sm:text-sm" data-test-accepted-formats>
            Accepted formats: .pdf, .jpg, .png.
          </p>
        {{/if}}

        {{#if this.uploadErrorMessage}}
          <p class="text-xs text-red-50 mt-1 mb-2" data-test-manage-upload-error role="alert" aria-live="assertive">
            {{this.uploadErrorMessage}}
          </p>
        {{/if}}
      {{else if this.manageUploadAwaitingReload}}
        {{! A manual refresh rather than the misleading "not uploaded" copy, and never the dropzone,
            which a re-drop would turn into a duplicate auto-approved doc. }}
        <div class="py-8 text-center text-carbon-50" data-test-manage-upload-awaiting-reload>
          <p class="mb-2">Document uploaded.</p>
          <button
            type="button"
            class="btn--link !text-gem-50 text-sm"
            data-test-manage-upload-refresh
            {{on "click" this.retryManageReload}}
          >
            Refresh to view
          </button>
        </div>
      {{else}}
        {{! Not uploaded placeholder }}
        <div class="py-8 text-center text-carbon-50" data-test-not-uploaded-placeholder>
          Document not yet uploaded
        </div>
      {{/if}}

      {{! ===== Shared editable expiration rows (review-uploaded OR create-completed) ===== }}
      {{#if this.showExpirationRows}}
        {{! template-lint-disable use-ui-card }}
        <div
          class="mt-4 bg-white rounded-lg border border-carbon-10 p-4"
          data-test-expiration-fields
          {{this.captureExpirationFields}}
          {{this.seedFromControlledArgs @status @fieldValues}}
          {{this.seedFromDocumentData @document.inputFieldsData}}
        >
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-carbon-50 leading-none">Expiration date(s)</span>
            <button
              class="text-sm text-gem-50 hover:underline"
              type="button"
              data-test-add-expiration
              {{on "click" this.addExpirationRow}}
            >
              Add
            </button>
          </div>

          {{#if this.parseReturnedNoDates}}
            <p class="text-xs text-carbon-50 mb-2" data-test-no-dates-detected>
              No expiration dates were detected — add them below if this document has any.
            </p>
          {{/if}}

          {{! Keyed by index: edits replace the entry objects, so identity keying would rebuild every
              row on each edit and drop keyboard focus mid-entry. }}
          {{#each this.expirationEntries key="@index" as |entry index|}}
            <div class="flex items-center gap-2 mb-3" data-test-expiration-row>
              <input
                type="text"
                value={{entry.name}}
                placeholder="Name"
                aria-label={{this.expirationNameLabel index}}
                class="flex-1 text-sm border rounded px-3 h-10 !mb-0
                  {{if (and (not entry.name) entry.date) 'border-red-50' 'border-carbon-20'}}"
                aria-invalid={{if (and (not entry.name) entry.date) "true" "false"}}
                aria-describedby={{if (and (not entry.name) entry.date) (this.expirationNameErrorId index)}}
                data-test-expiration-name
                data-private
                {{on "change" (fn this.updateExpirationName index)}}
              />
              {{! Reason for the immediate aria-invalid; the visible alert below stays deferred. }}
              {{#if (and (not entry.name) entry.date)}}
                <p id={{this.expirationNameErrorId index}} class="sr-only" data-test-expiration-name-error>
                  Name is required for this expiration date.
                </p>
              {{/if}}
              <div class="relative">
                <button
                  type="button"
                  class="flex items-center justify-start gap-1.5 w-[130px] px-3 text-sm border rounded bg-white cursor-pointer transition-colors duration-150 hover:border-carbon-40 h-10
                    {{if (eq this.openCalendar index) 'border-gem-50' 'border-carbon-20'}}"
                  aria-label={{this.expirationDateLabel index}}
                  aria-haspopup="true"
                  aria-expanded={{if (eq this.openCalendar index) "true" "false"}}
                  data-test-expiration-date
                  {{on "click" (fn this.toggleCalendar index)}}
                >
                  <span class="flex-none flex items-center text-carbon-40">{{svgJar
                      "calendar-16"
                      width="16"
                      height="16"
                    }}</span>
                  <span class="{{unless entry.date 'text-carbon-40'}}">{{this.formatDate entry.date}}</span>
                </button>
                {{! template-lint-disable use-ui-card }}
                {{#if (eq this.openCalendar index)}}
                  <div
                    class="absolute right-0 z-50 mt-1 p-3 bg-white border border-carbon-20 rounded-lg shadow-lg"
                    data-test-expiration-calendar
                  >
                    <EnvoyOneUpCalendar
                      @selectedDate={{entry.date}}
                      @onSelectDate={{fn this.selectExpirationDate index}}
                    />
                  </div>
                {{/if}}
              </div>
              {{#if this.canRemoveRow}}
                <button
                  class="flex-none w-8 h-10 flex items-center justify-center text-carbon-70 transition-opacity hover:opacity-75"
                  type="button"
                  data-test-remove-expiration
                  {{on "click" (fn this.removeExpirationRow index)}}
                >
                  {{svgJar "cross-black" width="16" height="16" class="[&_path]:fill-current"}}
                </button>
              {{else}}
                <div class="flex-none w-8 h-10"></div>
              {{/if}}
            </div>
          {{/each}}
          {{#if this.showIncompleteRowError}}
            <p class="text-xs text-red-50 mt-1" data-test-expiration-validation role="alert" aria-live="assertive">
              Both name and date are required for each entry.
            </p>
          {{/if}}
          {{#if this.saveErrorMessage}}
            <p class="text-xs text-red-50 mt-1" data-test-expiration-save-error role="alert" aria-live="assertive">
              {{this.saveErrorMessage}}
            </p>
          {{/if}}
        </div>
      {{/if}}

      {{! ===== Approval actions: review and manage only. An admin-uploaded doc is already
          approved, so it shows none. ===== }}
      {{#if (and @isUploaded (not this.isCreateMode))}}
        {{#if this.showDecisionActions}}
          {{#if (and this.requiresExpirationDate (not this.hasAnyCompleteDate))}}
            <p class="text-xs text-red-50 mt-2 mb-0" data-test-expiration-required role="alert" aria-live="assertive">
              At least one expiration date is required before approving.
            </p>
          {{/if}}
          {{! Phone: approve/deny live only in the preview's sticky bar. }}
          <DocumentReviewActions
            class="mt-4 hidden sm:flex"
            @onApprove={{this.approveTask.perform}}
            @onDeny={{this.denyTask.perform}}
            @approveDisabled={{this.approveDisabled}}
            @denyDisabled={{this.denyTask.isRunning}}
          />
        {{else if (and this.isDecided @showUndo)}}
          <div class="flex mt-4" data-test-document-actions>
            <button
              class="px-4 py-[11px] bg-white border border-carbon-20 rounded-md text-sm font-medium text-carbon-70 hover:bg-carbon-5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-test-undo-button
              disabled={{this.resetTask.isRunning}}
              type="button"
              {{on "click" this.resetTask.perform}}
            >
              Undo
            </button>
          </div>
        {{/if}}
      {{/if}}

      {{! Just hosts the modal; the header's trash icon opens it. }}
      {{#if this.showDeleteAffordance}}
        <DocumentDeleteConfirmationModal
          @isOpen={{this.showDeleteConfirm}}
          @onConfirm={{this.deleteDocumentTask.perform}}
          @onCancel={{this.closeDeleteConfirm}}
          @isDeleting={{this.deleteDocumentTask.isRunning}}
          @errorMessage={{this.deleteErrorMessage}}
        />
      {{/if}}
    </UiCard>
  </template>
}
